// Admin Leads page — shows ALL captured leads (from cards & microsites), inside the existing admin.
import React, { useEffect, useState, useCallback } from 'react';
import crm, { STAGES, PRIORITIES } from '../crm/api';
import LeadDetail from '../crm/LeadDetail';
import KanbanBoard from '../crm/KanbanBoard';
import LeadGrid from '../crm/LeadGrid';

const statusColor = (s) => (STAGES.find((x) => x.key === s) || {}).color || '#64748b';
const prio = (p) => PRIORITIES.find((x) => x.key === p) || PRIORITIES[1];
const inr = (v) => (v ? '₹' + Number(v).toLocaleString('en-IN') : '—');

export default function LeadsViewer() {
  const [rows, setRows] = useState([]);
  const [pag, setPag] = useState({ total: 0, page: 1, pages: 1 });
  const [stages, setStages] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(new URLSearchParams(window.location.search).get('view') || 'table');
  const [campaigns, setCampaigns] = useState([]);
  const [campaign, setCampaign] = useState('');
  const [employees, setEmployees] = useState([]);
  const [owners, setOwners] = useState([]);       // employee-wise multi-select (owner ids)
  const [ownerOpen, setOwnerOpen] = useState(false);

  useEffect(() => { crm.get('/campaigns').then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {}); }, []);
  useEffect(() => { crm.get('/employees', { params: { limit: 200 } }).then(({ data }) => setEmployees(data.employees || data.users || [])).catch(() => {}); }, []);

  const ownerParam = owners.length ? owners.join(',') : undefined;
  const toggleOwner = (id) => { setPage(1); setOwners((o) => o.includes(id) ? o.filter((x) => x !== id) : [...o, id]); };

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      crm.get('/leads', { params: { q: q || undefined, status: status || undefined, campaign: campaign || undefined, owner: ownerParam, page } }),
      crm.get('/pipeline'),
    ]).then(([l, p]) => {
      setRows(l.data.leads); setPag(l.data.pagination);
      setStages(p.data.stages); setLoading(false);
    }).catch(() => setLoading(false));
  }, [q, status, campaign, ownerParam, page]);
  useEffect(() => { load(); }, [load]);
  // deep-link: /leads?open=<id> opens that lead's 360 view
  useEffect(() => { const o = new URLSearchParams(window.location.search).get('open'); if (o) setOpenId(Number(o)); }, []);

  const exportCSV = async () => {
    const { data } = await crm.get('/leads', { params: { q: q || undefined, status: status || undefined, campaign: campaign || undefined, owner: ownerParam, limit: 100, page: 1 } });
    const all = data.leads || rows;
    const cols = ['name', 'company', 'city', 'phone', 'email', 'interest', 'value', 'priority', 'status', 'source'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [cols.join(','), ...all.map((l) => cols.map((c) => esc(c === 'owner' ? l.owner?.name : l[c])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const u = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = u; a.download = `dfc-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(u);
  };

  // Blank template with headers + one example row, so users know the upload format.
  const downloadTemplate = () => {
    const cols = ['name', 'company', 'email', 'phone', 'interest', 'value', 'priority', 'status', 'source'];
    const example = 'Dr. Example,Example Clinic,dr@example.com,9000000000,Neuropathy Monofilament,250000,hot,new,microsite';
    const blob = new Blob([cols.join(',') + '\n' + example + '\n'], { type: 'text/csv' });
    const u = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = u; a.download = 'dfc-leads-template.csv'; a.click(); URL.revokeObjectURL(u);
  };

  const total = pag.total;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-xl p-1" style={{ background: 'var(--soft)' }}>
            {['table', 'grid', 'board'].map((v) => (
              <button key={v} onClick={() => setView(v)} className="px-4 py-1.5 rounded-lg text-[13px] font-bold capitalize transition-colors"
                style={view === v ? { background: 'var(--card)', color: 'var(--acc2)', boxShadow: '0 1px 3px rgba(0,0,0,.12)' } : { color: 'var(--muted)' }}>{v}</button>
            ))}
          </div>
          {view !== 'board' && (
            <select value={campaign} onChange={(e) => { setPage(1); setCampaign(e.target.value); }}
              className="px-3 py-1.5 border border-[var(--border)] rounded-lg text-[13px] font-semibold bg-[var(--card)] text-[var(--text)]" title="Filter by campaign">
              <option value="">All campaigns</option>
              <option value="none">Direct (no campaign)</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {view !== 'board' && (
            <div className="relative">
              <button onClick={() => setOwnerOpen((v) => !v)} title="Filter by employee"
                className="px-3 py-1.5 border border-[var(--border)] rounded-lg text-[13px] font-semibold bg-[var(--card)] text-[var(--text)] flex items-center gap-1.5">
                {owners.length ? `${owners.length} employee${owners.length > 1 ? 's' : ''}` : 'All employees'}
                <span className="text-[10px] text-[var(--muted)]">▼</span>
              </button>
              {ownerOpen && (<>
                <div className="fixed inset-0 z-10" onClick={() => setOwnerOpen(false)} />
                <div className="absolute z-20 mt-1 w-64 max-h-72 overflow-y-auto rounded-xl border border-[var(--border)] bg-[var(--card)] card-lux p-2">
                  <div className="flex items-center justify-between px-2 py-1">
                    <span className="text-[11px] font-bold uppercase text-[var(--muted)]">Employees</span>
                    {owners.length > 0 && <button onClick={() => { setPage(1); setOwners([]); }} className="text-[11px] font-semibold text-[var(--acc2)] hover:underline">Clear</button>}
                  </div>
                  {employees.map((e) => (
                    <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--soft)] cursor-pointer text-[13px] text-[var(--text)]">
                      <input type="checkbox" checked={owners.includes(e.id)} onChange={() => toggleOwner(e.id)} className="accent-[var(--acc2)]" />
                      <span className="truncate">{e.name}</span>
                    </label>
                  ))}
                  {employees.length === 0 && <div className="px-2 py-2 text-[12px] text-[var(--muted)]">No employees</div>}
                </div>
              </>)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={exportCSV} className="text-sm font-semibold px-3 py-1.5 rounded-lg border border-[var(--border)] text-[var(--text)] hover:bg-[var(--soft)]">⬇ Export CSV</button>
          <button onClick={downloadTemplate} className="text-sm font-semibold text-[var(--acc2)] hover:underline" title="Download a blank CSV with the correct columns">Sample CSV</button>
          <button onClick={load} className="text-sm font-semibold text-[var(--acc2)] hover:underline">Refresh</button>
        </div>
      </div>

      {/* stat chips per stage */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-5">
        {[{ key: '', label: 'All', color: '#64748b', count: total },
          ...STAGES.map((s) => ({ ...s, count: (stages.find((x) => x.key === s.key) || {}).count || 0 }))
        ].map((s) => {
          const on = status === s.key;
          return (
            <button key={s.key || 'all'} onClick={() => { setStatus(s.key); setPage(1); }}
              className="rounded-2xl border card-lux p-3 text-center transition-colors"
              // Tinted in the stage's own colour rather than flat card-black.
              // Alpha over --card so it stays light on light themes and lifts off the ground on dark ones.
              style={{
                backgroundColor: 'var(--card)',
                backgroundImage: `linear-gradient(150deg, ${s.color}${on ? '4D' : '2E'}, ${s.color}${on ? '2E' : '17'})`,
                borderColor: `${s.color}${on ? 'CC' : '66'}`,
              }}>
              <div className="text-xl font-extrabold" style={{ color: s.color }}>{s.count}</div>
              <div className="text-[11px] font-semibold text-[var(--muted)]">{s.label}</div>
            </button>
          );
        })}
      </div>

      {view === 'board' ? <KanbanBoard /> : view === 'grid' ? (
        <LeadGrid leads={rows} onOpen={setOpenId} />
      ) : (<>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search name / email / phone / company"
            className="px-3 py-2 border border-[var(--border)] rounded-lg text-sm w-72 focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
          {status && <button onClick={() => setStatus('')} className="text-sm text-[var(--muted)] hover:text-teal-700">Clear filter ✕</button>}
        </div>

        <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux overflow-x-auto">
          <table className="w-full text-sm min-w-[920px]">
            <thead className="bg-[var(--soft)] text-[var(--muted)] text-xs uppercase">
              <tr>{['Name', 'Company', 'City', 'Phone', 'Email', 'Interest', 'Value', 'Priority', 'Owner', 'Status', 'Source'].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="px-4 py-8 text-center text-[var(--muted)]">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-[var(--muted)]">No leads yet. They appear here when a prospect submits Contact Me / WhatsApp on a card or microsite.</td></tr>}
              {rows.map((l) => (
                <tr key={l.id} onClick={() => setOpenId(l.id)} className="border-t border-[var(--border)] hover:bg-[var(--soft)] cursor-pointer">
                  <td className="px-4 py-2.5 font-semibold text-[var(--text)]">{l.name || '—'}</td>
                  <td className="px-4 py-2.5 text-[var(--muted)]">{l.company || '—'}</td>
                  <td className="px-4 py-2.5 text-[var(--muted)]">{l.city || '—'}</td>
                  <td className="px-4 py-2.5 text-[var(--text)] whitespace-nowrap">{l.phone ? <a href={`tel:${l.phone}`} onClick={(e) => e.stopPropagation()} className="hover:underline">{l.phone}</a> : '—'}</td>
                  <td className="px-4 py-2.5 text-[var(--muted)]">{l.email || '—'}</td>
                  <td className="px-4 py-2.5 text-[var(--muted)]">{l.interest || '—'}</td>
                  <td className="px-4 py-2.5 font-semibold text-[var(--text)]">{inr(l.value)}</td>
                  <td className="px-4 py-2.5"><span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ color: prio(l.priority).color, background: prio(l.priority).color + '1a' }}>{prio(l.priority).dot}{prio(l.priority).label}</span></td>
                  <td className="px-4 py-2.5 text-[var(--muted)]">{l.owner?.name || '—'}</td>
                  <td className="px-4 py-2.5"><span className="text-[11px] font-bold text-white px-2 py-0.5 rounded-full" style={{ background: statusColor(l.status) }}>{l.status}</span></td>
                  <td className="px-4 py-2.5 text-[var(--muted)] text-xs">{l.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pag.pages > 1 && (
          <div className="flex justify-center gap-2 mt-4 text-sm">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded-lg border disabled:opacity-40">Prev</button>
            <span className="px-3 py-1.5">Page {pag.page} / {pag.pages}</span>
            <button disabled={page >= pag.pages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded-lg border disabled:opacity-40">Next</button>
          </div>
        )}
      </>)}

      {openId && <LeadDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
